import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiErrorDetail } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Upload, Download, Search, Edit, Trash2, ArrowLeft } from 'lucide-react';

const EntryManagement = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchId, setSearchId] = useState('');
  const [searchUsername, setSearchUsername] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    sugo_id: '',
    username: '',
    admin_note: '',
    is_test: true,
  });

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchId) params.append('sugo_id', searchId);
      if (searchUsername) params.append('username', searchUsername);
      if (filterStatus) params.append('status', filterStatus);

      const { data } = await api.get(`/entries?${params.toString()}`);
      setEntries(data);
    } catch (error) {
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadEntries();
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();

    const quantity = Number(formData.ticket_quantity);

    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 500) {
      toast.error('Ticket Quantity must be between 1 and 500');
      return;
    }

    try {
      const payload = {
        sugo_id: formData.sugo_id.trim(),
        username: formData.username.trim() || null,
        entry_date: formData.entry_date
          ? `${formData.entry_date}T00:00:00.000Z`
          : null,
        ticket_quantity: quantity,
        admin_note: formData.admin_note.trim() || null,
        is_test: formData.is_test
      };

      const { data } = await api.post('/entries/bulk', payload);

      toast.success(
        `${data?.created_count || quantity} ticket(s) added successfully`
      );

      setShowAddDialog(false);
      setFormData({
        sugo_id: '',
        username: '',
        entry_date: new Date().toISOString().slice(0, 10),
        ticket_quantity: 1,
        admin_note: '',
        is_test: true
      });
      loadEntries();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to add entry');
    }
  };

  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/entries/${editingEntry.id}`, formData);
      toast.success('Entry updated successfully');
      setEditingEntry(null);
      setFormData({ sugo_id: '', username: '', admin_note: '', is_test: true });
      loadEntries();
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      await api.delete(`/entries/${id}`);
      toast.success('Entry deleted successfully');
      loadEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/entries/import-template', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      );

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'CEOS_Entry_Import_Template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
      toast.success('Import template downloaded');
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
        'Failed to download template'
      );
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post('/entries/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message);
      loadEntries();
    } catch (error) {
      toast.error('Failed to import CSV');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/entries/export-csv', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'entries.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      sugo_id: entry.sugo_id,
      username: entry.username || '',
      admin_note: entry.admin_note || '',
      is_test: entry.is_test !== undefined ? entry.is_test : true,
    });
  };

  return (
    <div className="min-h-screen bg-[#080808] pb-12">
      {/* Header */}
      <div className="bg-[#121212] border-b border-[#D4AF37]/20 px-6 py-4 mb-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-white">Entry Management</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Actions */}
        <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6 mb-6">
          <div className="flex flex-wrap gap-4 mb-6">
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]"
              data-testid="btn-add-entry"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
                      <Button
            type="button"
            onClick={handleDownloadTemplate}
            variant="outline"
            className="border-[#F3A6C8]/30 text-[#F3A6C8]"
            data-testid="btn-download-import-template"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>

<Button
            type="button"
            onClick={() =>
              document.getElementById('entry-import-file')?.click()
            }
            variant="outline"
            className="border-[#D4AF37]/30 text-[#D4AF37]"
            data-testid="btn-import-spreadsheet"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Spreadsheet
          </Button>

          <input
            id="entry-import-file"
            type="file"
            accept=".csv,text/csv"
            onClick={(e) => {
              e.currentTarget.value = '';
            }}
            onChange={handleImportCSV}
            className="hidden"
            data-testid="input-import-spreadsheet"
          />
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="border-[#D4AF37]/30 text-[#D4AF37]"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Detailed CSV
            </Button>
          </div>

          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              placeholder="Search by Sugo ID"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className="bg-[#080808] border-[#D4AF37]/30 text-white"
              data-testid="input-search-sugo-id"
            />
            <Input
              placeholder="Search by Sugo Name"
              value={searchUsername}
              onChange={(e) => setSearchUsername(e.target.value)}
              className="bg-[#080808] border-[#D4AF37]/30 text-white"
            />
            <Select value={filterStatus || 'all'} onValueChange={(val) => setFilterStatus(val === 'all' ? '' : val)}>
              <SelectTrigger className="bg-[#080808] border-[#D4AF37]/30 text-white">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#D4AF37]/30">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Used">Used</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={handleSearch}
              className="bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]"
              data-testid="btn-search"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </Card>

        {/* Entries Table */}
        <Card className="bg-[#121212] border border-[#D4AF37]/10">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#D4AF37]/20">
                  <TableHead className="text-[#D4AF37]">Sugo ID</TableHead>
                  <TableHead className="text-[#D4AF37]">Sugo Name</TableHead>
                  <TableHead className="text-[#D4AF37]">Status</TableHead>
                  <TableHead className="text-[#D4AF37]">Prize Won</TableHead>
                  <TableHead className="text-[#D4AF37]">Entry Date</TableHead>
                  <TableHead className="text-[#D4AF37]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-[#A0A0A0] py-8">
                      Loading entries...
                    </TableCell>
                  </TableRow>
                ) : entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-[#A0A0A0] py-8">
                      No entries found
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => (
                    <TableRow key={entry.id} className="border-[#D4AF37]/10">
                      <TableCell className="text-white font-semibold">{entry.sugo_id}</TableCell>
                      <TableCell className="text-white">{entry.username || '-'}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            entry.status === 'Active'
                              ? 'bg-[#D4AF37] text-[#080808]'
                              : 'bg-[#121212] text-[#A0A0A0] border border-[#A0A0A0]/30'
                          }
                        >
                          {entry.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#F3A6C8]">{entry.prize_won || '-'}</TableCell>
                      <TableCell className="text-[#A0A0A0]">
                        {new Date(entry.entry_date).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(entry)}
                            className="border-[#D4AF37]/30 text-[#D4AF37]"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="border-red-500/30 text-red-500"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      {/* Add Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#121212] border-[#D4AF37]/20 text-white">
          <DialogHeader>
            <DialogTitle>Add New Entry</DialogTitle>
            <DialogDescription className="text-[#A0A0A0]">
              Add a new Lucky Draw entry. Test entries can be safely cleared later.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddEntry} className="space-y-4">
            <div>
              <Label htmlFor="sugo_id">Sugo ID *</Label>
              <Input
                id="sugo_id"
                value={formData.sugo_id}
                onChange={(e) => setFormData({ ...formData, sugo_id: e.target.value })}
                required
                className="bg-[#080808] border-[#D4AF37]/30 text-white"
                data-testid="input-add-sugo-id"
              />
            </div>
            <div>
              <Label htmlFor="username">Sugo Name</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-[#080808] border-[#D4AF37]/30 text-white"
              />
            </div>
            <div>
            <Label htmlFor="entry_date">Date *</Label>
            <Input
              id="entry_date"
              type="date"
              value={formData.entry_date || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  entry_date: e.target.value
                })
              }
              required
              className="bg-[#080808] border-[#D4AF37]/30 text-white"
              data-testid="input-add-entry-date"
            />
          </div>

          <div>
            <Label htmlFor="ticket_quantity">Ticket Quantity *</Label>
            <Input
              id="ticket_quantity"
              type="number"
              min="1"
              max="500"
              step="1"
              value={formData.ticket_quantity}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  ticket_quantity: e.target.value
                })
              }
              required
              className="bg-[#080808] border-[#D4AF37]/30 text-white"
              data-testid="input-add-ticket-quantity"
            />
            <p className="text-xs text-[#A0A0A0] mt-1">
              Minimum 1, maximum 500. Quantity 10 creates 10 separate Lucky Draw tickets.
            </p>
          </div>

          <div>
              <Label htmlFor="admin_note">Admin Note</Label>
              <Input
                id="admin_note"
                value={formData.admin_note}
                onChange={(e) => setFormData({ ...formData, admin_note: e.target.value })}
                className="bg-[#080808] border-[#D4AF37]/30 text-white"
              />
            </div>
            <div className="flex items-center justify-between bg-[#080808] p-3 rounded-lg border border-[#F3A6C8]/20">
              <div>
                <Label htmlFor="is_test" className="text-white">Mark as Test Data</Label>
                <p className="text-xs text-[#A0A0A0] mt-1">
                  When enabled, this entry can be safely removed via "Clear Test Data".
                </p>
              </div>
              <Switch
                id="is_test"
                checked={formData.is_test}
                onCheckedChange={(checked) => setFormData({ ...formData, is_test: checked })}
                data-testid="switch-is-test"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]"
              data-testid="btn-submit-add-entry"
            >
              Add Entry
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent className="bg-[#121212] border-[#D4AF37]/20 text-white">
          <DialogHeader>
            <DialogTitle>Edit Entry</DialogTitle>
            <DialogDescription className="text-[#A0A0A0]">
              Update the entry details below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateEntry} className="space-y-4">
            <div>
              <Label htmlFor="edit_sugo_id">Sugo ID *</Label>
              <Input
                id="edit_sugo_id"
                value={formData.sugo_id}
                onChange={(e) => setFormData({ ...formData, sugo_id: e.target.value })}
                required
                className="bg-[#080808] border-[#D4AF37]/30 text-white"
              />
            </div>
            <div>
              <Label htmlFor="edit_username">Sugo Name</Label>
              <Input
                id="edit_username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-[#080808] border-[#D4AF37]/30 text-white"
              />
            </div>
            <div>
              <Label htmlFor="edit_admin_note">Admin Note</Label>
              <Input
                id="edit_admin_note"
                value={formData.admin_note}
                onChange={(e) => setFormData({ ...formData, admin_note: e.target.value })}
                className="bg-[#080808] border-[#D4AF37]/30 text-white"
              />
            </div>
            <div className="flex items-center justify-between bg-[#080808] p-3 rounded-lg border border-[#F3A6C8]/20">
              <div>
                <Label htmlFor="edit_is_test" className="text-white">Mark as Test Data</Label>
                <p className="text-xs text-[#A0A0A0] mt-1">
                  When enabled, this entry can be safely removed via "Clear Test Data".
                </p>
              </div>
              <Switch
                id="edit_is_test"
                checked={formData.is_test}
                onCheckedChange={(checked) => setFormData({ ...formData, is_test: checked })}
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8]"
            >
              Update Entry
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntryManagement;
